// « Comprendre ce chiffre » — LE point d'entrée pédagogique de l'observatoire (lot P, 15/09).
//
// Demande de Marc : « On aimerait plus de pédagogie sur notre site. Ça serait notre moat
// versus des concurrents qui feraient juste de l'agrégation d'open data : quand on clique
// sur les tuiles ou les graphiques, que ça fasse une explication de la donnée, son scope,
// sa source, etc. Créer de la plus-value pour les mairies qui n'ont pas les compétences
// techniques. » Et : « standardiser les infobulles / explications des sources et des
// données ».
//
// CE QU'IL REMPLACE. Deux mécanismes coexistaient sans se parler :
//   · `DefTerm` — une bulle au survol, texte court, sur le LIBELLÉ de 64 tuiles sur 178 ;
//   · `FicheBtn` — un ⓘ de 18 px ouvrant une modale, sur 67 tuiles et 2 graphiques.
// Deux gestes, deux rendus, deux contenus, jamais les deux au même endroit — et 9 cartes
// qui n'avaient ni l'un ni l'autre (inventaire : docs/produit/2026-09-15-pedagogie-inventaire.md).
// Un élu qui veut comprendre un chiffre devait savoir LEQUEL des deux chercher.
//
// LE STANDARD. Un seul geste : on clique le petit bouton (i) « Comprendre ce chiffre », en
// bas de l'objet (tuile, carte du graphique, carte cartographique), un panneau s'ouvre sur
// la droite, et il dit toujours les mêmes choses dans le même ordre, sous les mêmes titres :
//
//   1. Ce que ça mesure               ← DEFS.full, à défaut DEFS.short, à défaut la
//                                        `definition` d'une fiche du socle (26/09)
//   2. D'où vient le chiffre          ← la fiche (source, producteur, millésime, cadence),
//                                        à défaut DEFS.source, à défaut les sources de la
//                                        section
//   3. Comment il est calculé         ← fiche.methode + les note_methode des échelles
//   4. Ce qu'il ne dit pas            ← fiche.limites + fiche.champ
//   5. Pourquoi ça compte pour une commune ← POURQUOI, et SEULEMENT au statut `valide`
//
// AUCUN LIEN SORTANT — décision de Marc, 15/09 00 h 20 : « Pas de lien officiel. Ils ne
// doivent pas sortir de la plateforme. Mais nous on est là pour leur dire d'où ça vient. »
// La source est donc NOMMÉE en toutes lettres (producteur, jeu de données, millésime,
// cadence) sans adresse ni détail de collecte (décision du 24/10/2026).
//
// ZÉRO-INVENTION, ET C'EST LA RÈGLE QUI COMMANDE TOUT LE FICHIER. Aucune rubrique n'est
// composée, complétée ou devinée ici : chaque ligne vient de DEFS, de FICHES, d'une
// `note_methode` servie ou du registre des sources. **Une rubrique sans contenu n'apparaît
// pas** — un titre suivi du vide affirmerait qu'il n'y a rien à dire, alors qu'il n'y a
// rien d'ÉCRIT. Et quand il n'y a rien du tout, le panneau le dit en toutes lettres
// (« Fiche en cours de rédaction ») au lieu de faire semblant.
//
// Le survol reste, en APERÇU : `DefTerm` continue d'afficher sa bulle courte sur le libellé.
//
// LE DÉCLENCHEUR (Marc, 30/09) — LE BOUTON (i), ET LUI SEUL. Du 15/09 au 30/09, un clic
// n'importe où sur la tuile ou la carte ouvrait le panneau. À l'usage, c'était le défaut
// d'une cible trop large : on clique une tuile pour la lire, pour sélectionner un chiffre,
// pour poser le focus — et le panneau s'ouvrait par mégarde, en pleine lecture. Le panneau
// ne s'ouvre donc plus que depuis le bouton « Comprendre ce chiffre » posé en bas de
// l'objet : un vrai `<button>`, dans l'ordre de tabulation, Entrée et Espace natifs, nommé
// par l'indicateur. Le reste de l'objet redevient une surface de LECTURE ; l'épingle, le
// partage, les liens et la bascule graphique / tableau gardent chacun leur propre geste.
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { usePiegeFocus } from './piegeFocus'
import type { ReactNode } from 'react'
import { Info, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { NOM_PRODUIT } from '../constants'
import { DEFS } from './defs'
import { useFiche, type Fiche } from './FicheTechnique'
import { pourquoiValide } from './pourquoi'
import { dateFr } from './projetsNatures'
import { useCanalTerObs } from './commentaires/contexte'
import { FenetreCommentaire } from './commentaires/FenetreCommentaire'
import { CommenterElement } from './commentaires/Commenter'
import { ancreDe, useFilsTerObs } from './commentaires/fils'
import type { SourceDetail } from './types'

/** Tout ce qu'un objet de l'observatoire sait dire de lui-même. Aucun champ n'est
    obligatoire : un objet qui n'a rien reste cliquable et le panneau l'assume. */
export interface CibleExplication {
  /** Le libellé de la tuile, le titre du graphique ou celui de la carte. */
  titre: string
  /** Clé du glossaire front (`k.def_term`). */
  defTerm?: string
  /** Clé du catalogue de fiches backend (`k.fiche`). */
  fiche?: string
  /** La note portée par la valeur elle-même (« Source : X, millésime Y »). */
  note?: string
  /** Les `note_methode` des échelles de comparaison, déjà dédoublonnées par l'appelant. */
  notesMethode?: string[]
  /** Les sources officielles de la SECTION — le dernier recours de la rubrique 2. */
  sources?: SourceDetail[]
  /** La fiche DÉJÀ résolue (page d'un lien partagé, CDC E2 : elle voyage dans l'instantané) —
      le panneau ne la redemande alors pas au serveur. */
  ficheFournie?: Fiche | null
  /** L'identifiant DÉCLARÉ du graphique (`<section>.<clé>`) : l'ancre d'un message « À TerObs »
      écrit depuis ce panneau (fiche I, 06/10). */
  graphique?: string
  /** Le libellé d'une TUILE (#563) : son ancre, que le serveur garde s'il est déclaré par le code
      des builders (sinon le message s'accroche à la section). Posé en `data-tuile` pour le lien
      profond et « Montrer » de la barre. */
  tuile?: string
}

function Rubrique({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <section className="obs2-expl-rub">
      <h4 className="obs2-expl-rub-t">{titre}</h4>
      {children}
    </section>
  )
}

/** Une ligne « intitulé : valeur » de la carte d'identité du chiffre. Rendue seulement si
    la valeur existe — la doctrine des fiches, reprise telle quelle. */
function Ligne({ t, v }: { t: string; v?: string | null }) {
  if (!v) return null
  return <p className="obs2-expl-l"><span className="k">{t}</span> {v}</p>
}

/** Le panneau lui-même. Exporté pour les tests ; en production on passe par `Explicable`. */
export function PanneauExplication({ cible, onClose }:
                                   { cible: CibleExplication; onClose: () => void }) {
  // La tuile cliquée est passée à la fiche : quand celle-ci sert plusieurs tuiles, elle
  // rend la méthode et les limites de CELLE-CI, pas celles de la famille (fiches.py).
  const chargee = useFiche(cible.ficheFournie ? undefined : cible.fiche, cible.defTerm)
  const fiche = cible.ficheFournie ?? chargee.fiche
  const erreur = cible.ficheFournie ? false : chargee.erreur
  const d = cible.defTerm ? DEFS[cible.defTerm] : undefined
  const panneau = useRef<HTMLDivElement>(null)
  const titreId = useId()
  // « Écrire à TerObs » (fiche I, 06/10) : proposé quand le canal est posé (abonné ou essai de
  // CETTE commune, hors équipe, drapeau allumé) ET qu'une ancre est connue (section, synthèse).
  // Sinon le lien « Signaler une erreur » vers /contact reste, à l'identique.
  const canal = useCanalTerObs()
  const peutEcrire = !!canal && canal.ecriture !== false && !!(canal.page || canal.section)
  const [ecrire, setEcrire] = useState(false)
  // Barre latérale « Mes commentaires » (06/10) : dans l'observatoire, « Écrire à TerObs » ferme
  // ce panneau et ouvre l'écriture DANS la barre, ancrée sur cet élément — un seul endroit pour
  // écrire et relire. Sans barre (tests, pages hors de l'observatoire), la fenêtre reste ici.
  const fils = useFilsTerObs()
  const ecrireATerObs = () => {
    const ancre = fils ? ancreDe(canal, cible.graphique, cible.tuile) : null
    if (!fils || !ancre) { setEcrire(true); return }
    onClose()
    // « Écrire à TerObs » est un geste explicite : la fenêtre s'ouvre réglée sur ce niveau
    // (changeable), jamais sur le niveau par défaut.
    fils.commenter({ ancre, titre: cible.titre, niveau: 'terobs' })
  }

  // Échap ferme, et le focus est PIÉGÉ dans le panneau (`usePiegeFocus`, partagé depuis le lot C avec le
  // tiroir des étapes Sitadel) : un lecteur au clavier qui tabule hors du panneau se retrouverait à
  // parcourir la page qu'il ne voit plus (le voile la recouvre) — WCAG 2.4.3 / 2.1.2.
  usePiegeFocus(panneau, onClose)

  // Le focus entre dans le panneau à l'ouverture : sans ce geste, un lecteur d'écran reste
  // sur la tuile et n'apprend jamais que quelque chose s'est ouvert.
  useEffect(() => { panneau.current?.focus() }, [])

  // ── Rubrique 1 : le glossaire d'abord ; une fiche du socle porte sa propre définition
  //    (celle du catalogue, posée par l'adaptateur depuis la fiche du producteur) ───────
  const definition = d ? (d.full || d.short) : fiche?.definition

  // ── Rubrique 2 : trois sources de vérité, dans l'ordre de précision décroissante ──────
  const aFiche = !!fiche
  const sourcesSection = cible.sources ?? []
  const provenance = aFiche || d?.source || sourcesSection.length > 0

  // ── Rubrique 3 ───────────────────────────────────────────────────────────────────────
  const notesMethode = [...new Set((cible.notesMethode ?? []).filter(Boolean))]
  const calcul = fiche?.methode || notesMethode.length > 0

  // ── Rubrique 4 ───────────────────────────────────────────────────────────────────────
  const limites = fiche?.limites || fiche?.champ

  // ── Rubrique 5 — éditorial : SEULS les textes validés par Cyrille sortent d'ici ──────
  // Une fiche du socle porte le sien, que le serveur ne sert QU'UNE FOIS relu
  // (`PEDAGOGIE[id].pourquoi_valide`, app/socle/vue.py) : même règle, autre registre.
  const pourquoi = pourquoiValide(cible.fiche, cible.defTerm) || fiche?.pourquoi

  const vide = !definition && !provenance && !calcul && !limites && !pourquoi

  return (
    <div className="obs2-expl-voile" onClick={onClose}>
      <div className="obs2-expl" role="dialog" aria-modal="true" aria-labelledby={titreId}
        ref={panneau} tabIndex={-1} onClick={e => e.stopPropagation()}>
        <header className="obs2-expl-head">
          <div>
            <div className="obs2-expl-kicker">
              {ecrire ? `Écrire à ${NOM_PRODUIT}` : 'Comprendre ce chiffre'}
            </div>
            <h3 className="obs2-expl-titre" id={titreId}>{d?.title || fiche?.label || cible.titre}</h3>
          </div>
          <button type="button" className="obs2-iconbtn obs2-expl-x" aria-label="Fermer"
            onClick={onClose}><X size={16} aria-hidden="true" /></button>
        </header>

        {ecrire && canal ? (
          <div className="obs2-expl-corps">
            <FenetreCommentaire canal={canal} graphique={cible.graphique} tuile={cible.tuile}
              titre={cible.titre} niveauInitial="terobs" onFermer={() => setEcrire(false)} />
          </div>
        ) : (
        <div className="obs2-expl-corps">
          {vide && !erreur && (
            <p className="obs2-expl-vide">
              Fiche en cours de rédaction. Cet indicateur n'a pas encore sa carte d'identité
              détaillée — nous ne la remplissons pas d'approximations en attendant.
            </p>
          )}

          {/* 1 ─ Ce que ça mesure */}
          {definition && (
            <Rubrique titre="Ce que ça mesure">
              <p>{definition}</p>
            </Rubrique>
          )}

          {/* 2 ─ D'où vient le chiffre */}
          {provenance && (
            <Rubrique titre="D'où vient le chiffre">
              {fiche ? (
                <>
                  {/* Une fiche du socle n'a pas toujours de nom de jeu de données : le
                      producteur seul, plutôt qu'un « undefined — INSEE ». */}
                  <Ligne t="Source" v={[fiche.source, fiche.producteur].filter(Boolean).join(' — ')} />
                  <Ligne t="Millésime des données" v={fiche.millesime && fiche.publie_le
                    ? `${fiche.millesime} — publié le ${dateFr(fiche.publie_le)}`
                    : fiche.millesime} />
                  <Ligne t="Mise à jour de la source" v={fiche.cadence} />
                  <Ligne t="Licence" v={fiche.licence} />
                  {/* Pas de ligne « jeu de données officiel » : ni lien, ni URL en texte.
                      Marc, 15/09 : « Pas de lien officiel. Ils ne doivent pas sortir de la
                      plateforme. » Le nom du producteur et celui de la base suffisent à
                      dire d'où ça vient — c'est ce qu'on nous demande de dire. */}
                </>
              ) : d?.source ? (
                <Ligne t="Source" v={d.source} />
              ) : null}
              {/* La note de la TUILE ne se recopie pas ici. Marc, 20/09, capture à l'appui :
                  elle ramenait dans « D'où vient le chiffre » la mise en garde que la
                  rubrique 4 porte déjà — le lecteur voyait deux fois le même paragraphe —
                  et y déversait des valeurs (« dont minima sociaux 1,3 % ») qui n'ont rien
                  à faire dans une rubrique de provenance. Cette rubrique dit d'OÙ vient le
                  chiffre : source, producteur, millésime, cadence. Rien d'autre. */}
              {!fiche && !d?.source && sourcesSection.length > 0 && (
                <Ligne t="Sources de la section"
                  v={sourcesSection.map(s => s.nom).join(' · ')} />
              )}
            </Rubrique>
          )}

          {/* 3 ─ Comment il est calculé */}
          {calcul && (
            <Rubrique titre="Comment il est calculé">
              {fiche?.methode && <p>{fiche.methode}</p>}
              {notesMethode.map((n, i) => <p key={i} className="obs2-expl-note">{n}</p>)}
            </Rubrique>
          )}

          {/* 4 ─ Ce qu'il ne dit pas */}
          {limites && (
            <Rubrique titre="Ce qu'il ne dit pas">
              {fiche?.limites && <p>{fiche.limites}</p>}
              <Ligne t="Champ couvert" v={fiche?.champ} />
            </Rubrique>
          )}

          {/* 5 ─ Pourquoi ça compte pour une commune */}
          {pourquoi && (
            <Rubrique titre="Pourquoi ça compte pour une commune">
              <p>{pourquoi}</p>
            </Rubrique>
          )}

          {erreur && (
            <p className="obs2-expl-note">
              Fiche technique momentanément indisponible — le reste de cette explication
              reste exact.
            </p>
          )}
        </div>
        )}

        <footer className="obs2-expl-pied">
          {peutEcrire ? (!ecrire && (
            <button type="button" className="obs2-expl-signal obs2-expl-ecrire"
              onClick={ecrireATerObs}>
              Une erreur, une question ? Écrire à {NOM_PRODUIT}
            </button>
          )) : <Link to="/contact" className="obs2-expl-signal">Signaler une erreur</Link>}
        </footer>
      </div>
    </div>
  )
}

/** Enveloppe un objet de l'observatoire et lui ajoute, en bas, le bouton (i) « Comprendre
    ce chiffre » — le SEUL déclencheur du panneau (Marc, 30/09). L'enveloppe garde la
    classe de l'objet : la tuile reste une `div.obs2-kpi`, la carte une `div.obs2-card`.

    Le conteneur n'a plus ni `role="button"`, ni `tabIndex`, ni gestionnaire de clic : la
    tuile entière comme cible (lot P, 15/09) ouvrait le panneau à chaque clic de lecture.
    Le bouton, lui, est un `<button>` natif — Entrée et Espace sans code, nom accessible qui
    commence par son texte visible (WCAG 2.5.3) et nomme l'indicateur, cible d'au moins
    24 px (WCAG 2.5.8, voir `.obs2-expl-hint`). À la fermeture, le focus lui revient. */
export function Explicable({ cible, className, style, actif = true, children }: {
  cible: CibleExplication
  className?: string
  style?: React.CSSProperties
  /** `false` en impression (PrintView) : le PDF ne clique pas, et un bouton n'a aucun
      sens dans un document papier. Le rendu visuel du contenu est identique. */
  actif?: boolean
  children: ReactNode
}) {
  const [ouvert, setOuvert] = useState(false)
  const declencheur = useRef<HTMLButtonElement>(null)
  // Barre « Mes commentaires » (06/10) : la bulle « Commenter » se montre au survol de l'objet
  // ET quand le focus clavier y entre — deux états, pour qu'un pointeur qui sort ne la cache
  // pas sous le clavier. Rien du tout hors canal (voir `CommenterElement`).
  const [survol, setSurvol] = useState(false)
  const [focusDedans, setFocusDedans] = useState(false)

  const fermer = useCallback(() => {
    setOuvert(false)
    // Le focus revient d'où il vient — sinon il retombe en tête de document et la lecture
    // au clavier recommence à zéro.
    declencheur.current?.focus()
  }, [])

  if (!actif) return <div className={className} style={style}>{children}</div>

  return (
    <>
      <div className={`${className ?? ''} obs2-explicable`.trim()} style={style}
        // L'identifiant déclaré du graphique : la barre y fait défiler la page (« montrer »).
        data-graphique={cible.graphique || undefined}
        data-tuile={!cible.graphique && cible.tuile ? cible.tuile : undefined}
        onMouseEnter={() => setSurvol(true)} onMouseLeave={() => setSurvol(false)}
        onFocus={() => setFocusDedans(true)}
        onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusDedans(false) }}>
        {children}
        <CommenterElement titre={cible.titre} graphique={cible.graphique} tuile={cible.tuile}
          visible={survol || focusDedans} />
        <button ref={declencheur} type="button" className="obs2-expl-hint"
          aria-haspopup="dialog" aria-label={`Comprendre ce chiffre : ${cible.titre}`}
          onClick={e => {
            // La tuile peut vivre dans un conteneur qui écoute les clics (la carte épinglée
            // glissable de la synthèse) : le geste s'arrête au bouton qui l'a reçu.
            e.stopPropagation()
            setOuvert(true)
          }}>
          <Info size={12} aria-hidden="true" />
          {/* Au repos, seul le (i) se voit ; le libellé se déplie au survol de l'objet et
              au focus (obs.css). Le nom accessible (`aria-label`) commence par ce même
              texte visible, puis nomme l'indicateur (WCAG 2.5.3). */}
          <span className="obs2-expl-hint-txt">Comprendre ce chiffre</span>
        </button>
      </div>
      {ouvert && <PanneauExplication cible={cible} onClose={fermer} />}
    </>
  )
}
